import {
  AfterViewInit,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  NgZone,
  OnInit,
  ViewEncapsulation,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { each, find, map as _map, reduce, sortBy } from 'lodash-es';
import { PropertyApiService } from 'apps/account/src/account-shared/services/property-api.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LivePreviewService } from 'apps/account/src/account-shared/services/live-preview.service';
import { NotificationsService } from 'apps/account/src/account-shared/services/notifications.service';
import { DEFAULT_LANGUAGE } from 'apps/account/src/account-shared/services/language-helper.util';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FtLanguageSelectorComponent } from '../contest-sheets/ft-language-selector.component';
import { FieldEditorModalConfig, FtFieldEditorModalComponent } from '../contest-sheets/field-editor-modal.component';
import { FtPendingChangesPanelComponent } from './ft-pending-changes-panel.component';
import { FtDisabledDirective } from './ft-disabled.directive';
import { computeDiffs, revertDiffs, snapshotClone } from '../contest-sheets/native-diff.util';

/**
 * Native port of the AngularJS warm-lead certificate-email editor. Read-view: an 8-item carousel of
 * email types (reproduced natively), the email editor-preview (subject/body/signature/certificate-link),
 * a language selector, and the contest-content panel. The subject/body/contest-content field editors
 * open the native field-editor modal; a native pending-changes panel (deferred-save) commits via
 * saveChanges/getUpdates (full v1 union PUT). Loads its own data (Property.getEmailsData).
 */
@Component({
    selector: 'ft-touchpoint-certificate-email-ng',
    imports: [TranslateModule, FtLanguageSelectorComponent, FtFieldEditorModalComponent, FtPendingChangesPanelComponent, FtDisabledDirective],
    encapsulation: ViewEncapsulation.None,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    styleUrls: ['./ft-touchpoint-certificate-email.component.scss'],
    templateUrl: './ft-touchpoint-certificate-email.component.html'
})
export class FtTouchpointCertificateEmailComponent implements OnInit, AfterViewInit {
  readonly propertyUuid = input.required<string>();
  readonly touchpointType = input.required<string>();
  readonly emailTypes = input<any>();

  readonly panel = viewChild<FtPendingChangesPanelComponent>('panel');

  /** ftModal phase 2 / WS3: the native field-editor modal request (null = closed). */
  protected readonly fieldEditorConfig = signal<FieldEditorModalConfig | null>(null);
  /** Field-level open-time snapshot of selectedEmailData.languages, for the field modal's cancel-revert. */
  private fieldSnapshot: any[] | null = null;

  protected readonly model = signal<any>({ emailsData: [], languages: [], selectedLanguage: {} });
  protected readonly selectedEmailData = signal<any>({ languages: undefined, emailType: undefined });
  protected readonly carouselItems = signal<{ key: string; association: any }[]>([]);
  protected readonly carouselIndex = signal(0);

  private snapshotProxy: any;
  private updates: any[] = [];

  // The per-email-type token map (verbatim from EmailsDataController).
  private readonly tokensPerType: Record<number, string[]> = {
    11: ['visitor-firstname', 'guest-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-guest-photo', 'link-to-gallery'],
    7: ['friend-firstname', 'guest-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-guest-photo', 'link-to-gallery'],
    1: ['friend-firstname', 'guest-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-gallery', 'contest-content'],
    3: ['friend-firstname', 'guest-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-gallery', 'contest-content'],
    13: ['guest-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-gallery', 'contest-content'],
    22: ['guest-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-gallery', 'contest-content'],
    12: ['visitor-firstname', 'guest-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-guest-photo', 'link-to-gallery'],
    8: ['friend-firstname', 'guest-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-guest-photo', 'link-to-gallery'],
    2: ['friend-firstname', 'guest-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-gallery', 'contest-content'],
    4: ['friend-firstname', 'guest-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-gallery', 'contest-content'],
    23: ['guest-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-gallery', 'contest-content'],
    36: ['traveler-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-gallery', 'contest-content'],
    37: ['traveler-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-gallery', 'contest-content'],
    80: ['guest-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-gallery', 'contest-content', 'incentive-custom-code'],
    81: ['guest-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-gallery', 'contest-content', 'incentive-custom-code'],
  };

  // The carousel email-type carousel keys, in the certificateEmail.html order.
  private readonly carouselSpec: { key: string; type: string }[] = [
    { key: 'flipto.account.CertificateEmailCarouselContent.AnonymousPhotoSignupCertificate', type: 'AnonymousPhotoSignupCertificate' },
    { key: 'flipto.account.CertificateEmailCarouselContent.FriendPhotoSignupCertificate', type: 'FriendPhotoSignupCertificate' },
    { key: 'flipto.account.CertificateEmailCarouselContent.FriendPrestayHomepageSignupCertificate', type: 'FriendPrestayHomepageSignupCertificate' },
    { key: 'flipto.account.CertificateEmailCarouselContent.FriendPoststayHomepageSignupCertificate', type: 'FriendPoststayHomepageSignupCertificate' },
    { key: 'flipto.account.CertificateEmailCarouselContent.GuestPrestayPromotionCertificate', type: 'GuestPrestayPromotionCertificate' },
    { key: 'flipto.account.CertificateEmailCarouselContent.GuestPoststayPromotionCertificate', type: 'GuestPoststayPromotionCertificate' },
    { key: 'flipto.account.CertificateEmailCarouselContent.GuestPhotoFinalistCertificate', type: 'GuestPhotoFinalistCertificate' },
    { key: 'flipto.account.CertificateEmailCarouselContent.DefaultCertificate', type: 'DefaultCertificate' },
  ];

  // WS5 #10 inc 2t: FULLY drained — livePreview is the native LivePreviewService
  // (sendTestEmail/showTestEmail), `_` the same lodash module instance.
  private readonly property: any = inject(PropertyApiService);
  private readonly livePreview = inject(LivePreviewService);
  private readonly notifications = inject(NotificationsService);

  private readonly translateSvc = inject(TranslateService);
  private readonly zone = inject(NgZone);
  private readonly sanitizer = inject(DomSanitizer);

  ngOnInit(): void {
    // Build the carousel items (association = the numeric email-type id from the input map).
    this.carouselItems.set(this.carouselSpec.map((s) => ({ key: s.key, association: this.emailTypes()[s.type] })));
    Promise.resolve(this.property.getEmailsData(this.propertyUuid())).then((data: any) => {
      this.zone.run(() => {
        this.model().emailsData = data;
        this.model().languages = _map(this.model().emailsData[0].languages, (l: any) => ({ code: l.code, name: l.name }));
        each(this.model().emailsData, (d: any) => {
          d.languages = sortBy(d.languages, (lang: any) => (lang.code === 'en' ? '0' : lang.code));
        });
        // first email type, first language (the v1 ftLanguageSelector default-selects).
        this.carouselIndex.set(0);
        this.selectByEmailType(this.carouselItems()[0].association);
        this.model().selectedLanguage = find(this.model().languages, { code: DEFAULT_LANGUAGE }) || this.model().languages[0] || {};
        // The data just landed — THIS is the deferred-save baseline. A field modal opened
        // before the load would have setupIfNeeded() against the empty pre-load model, and
        // every loaded row would then read as a phantom pending change (the "72 changes"
        // bug); re-arm both the panel snapshot and any open field modal's cancel snapshot.
        // liveOverride is REQUIRED: the panel's `[live]` input still holds the pre-load
        // array until the next CD re-binds it — setup() without it snapshots the stale [].
        this.panel()?.setup(this.model().emailsData);
        if (this.fieldEditorConfig()) this.fieldSnapshot = snapshotClone(this.selectedEmailData().languages);
      });
    });
  }

  ngAfterViewInit(): void {
    // Native deferred-save panel (ft-pending-changes-panel-ng) is in the template — no bridge.
    // It snapshots lazily on first field-open (panel.setupIfNeeded), so the baseline is the stable,
    // fully-loaded model.emailsData (avoids stale-snapshot 'new element' diffs from load timing).
  }

  // --- read-view helpers ---

  protected trust(value: any): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(value || '');
  }

  /** The v1 `selectedEmailData.languages | arrayItem:'code':selectedLanguage.code` reads. */
  protected langData(field: string): any {
    const lang = find(this.selectedEmailData().languages, { code: this.model().selectedLanguage?.code });
    return lang ? lang[field] : undefined;
  }

  protected get hasContestContentToken(): boolean {
    const tokens = this.tokensPerType[this.selectedEmailData()?.emailType] || [];
    return tokens.indexOf('contest-content') !== -1;
  }

  // --- carousel (native reproduction of ft-carousel) ---

  protected carouselSelect(i: number): void {
    this.carouselIndex.set(i);
    this.selectByEmailType(this.carouselItems()[i].association);
  }
  protected carouselLeft(): void {
    if (this.canGoLeft()) this.carouselSelect(this.carouselIndex() - 1);
  }
  protected carouselRight(): void {
    if (this.canGoRight()) this.carouselSelect(this.carouselIndex() + 1);
  }
  protected canGoLeft(): boolean {
    return this.carouselIndex() > 0;
  }
  protected canGoRight(): boolean {
    return this.carouselIndex() < this.carouselItems().length - 1;
  }

  selectByEmailType(association: any): void {
    this.selectedEmailData.set(find(this.model().emailsData, (e: any) => e.emailType === association) || { languages: undefined, emailType: undefined });
  }

  // --- edit-modal bridges ---

  // ftModal phase 2 / WS3: the 3 cert-email field editors → native shell. They do NOT save
  // themselves — the native ft-pending-changes-panel-ng detects the edit (recompute on close) and
  // its Save commits. Field cancel reverts the field's own edit via fieldSnapshot.
  protected editSubject(): void {
    this.panel()?.setupIfNeeded();
    this.fieldSnapshot = snapshotClone(this.selectedEmailData().languages);
    this.fieldEditorConfig.set({
      type: 'text',
      // Getter = v1's LIVE `datasource="vm.selectedEmailData.languages"` binding: if
      // getEmailsData resolves while the modal is open, the language inputs appear
      // (a captured reference would freeze the empty pre-load array).
      datasource: () => this.selectedEmailData().languages,
      fields: [{ name: 'subject' }],
      fieldsOptions: { required: true },
      title: this.translateSvc.instant('flipto.account.Subject'),
      onCancel: () => this.rollbackField(),
      onClose: () => this.afterFieldClose(),
    });
  }

  protected editBody(): void {
    this.panel()?.setupIfNeeded();
    this.fieldSnapshot = snapshotClone(this.selectedEmailData().languages);
    this.fieldEditorConfig.set({
      type: 'html',
      datasource: () => this.selectedEmailData().languages,
      fields: [{ name: 'body' }],
      fieldsOptions: { required: true, tokens: this.tokensPerType[this.selectedEmailData().emailType] || [] },
      title: this.translateSvc.instant('flipto.account.Body'),
      onCancel: () => this.rollbackField(),
      onClose: () => this.afterFieldClose(),
    });
  }

  protected editContestContent(): void {
    this.panel()?.setupIfNeeded();
    this.fieldSnapshot = snapshotClone(this.selectedEmailData().languages);
    this.fieldEditorConfig.set({
      type: 'html',
      datasource: () => this.selectedEmailData().languages,
      fields: [{ name: 'contest_content' }],
      fieldsOptions: { tokens: this.tokensPerType[this.selectedEmailData().emailType] || [] },
      title: this.translateSvc.instant('flipto.account.ContestContent'),
      onCancel: () => this.rollbackField(),
      onClose: () => this.afterFieldClose(),
    });
  }

  /** Field cancel: revert the field's own edit (the panel's deferred snapshot is separate). */
  private rollbackField(): void {
    if (!this.fieldSnapshot) return;
    revertDiffs(this.selectedEmailData().languages, this.fieldSnapshot, computeDiffs(this.fieldSnapshot, this.selectedEmailData().languages));
  }

  private afterFieldClose(): void {
    this.fieldSnapshot = null;
    this.panel()?.recompute();
  }

  /**
   * v1 panel `inactive`: a hidden `emailData` ng-form validated the PENDING UPDATES
   * (required subject + body per update row) — Save & publish dims while any pending
   * update carries an empty subject or body. Recomputed per CD like the v1 form watch.
   */
  protected panelInactive(): boolean {
    const panel = this.panel();
    if (!panel) return false;
    const updates = this.getUpdates(panel.currentDiffs());
    return updates.some((u: any) => !u.subject || !u.body);
  }

  /** Panel Save → the v1 saveChanges (full union PUT) → re-snapshot. */
  protected onPanelSave(diffs: any[]): void {
    Promise.resolve(this.saveChanges(diffs)).then(() => this.panel()?.commit());
  }

  /** Panel Cancel: the panel already reverted model.emailsData; refresh the count. */
  protected onPanelCancel(): void {
    this.panel()?.recompute();
  }

  // --- live preview (ported; never auto-triggered — sendTestEmail SENDS a real email) ---

  protected sendTestEmail(): void {
    this.livePreview
      .sendTestEmail({ propertyUuid: this.propertyUuid(), emailType: this.selectedEmailData().emailType, languageCode: this.model().selectedLanguage.code })
      .then(
        () => this.notifications.show(this.translateSvc.instant('flipto.account.SendSampleEmailSuccess')),
        () => this.notifications.show(this.translateSvc.instant('flipto.account.SendSampleEmailError')),
      );
  }
  protected showEmailHtml(): void {
    this.livePreview.showTestEmail({ propertyUuid: this.propertyUuid(), emailType: this.selectedEmailData().emailType, languageCode: this.model().selectedLanguage.code });
  }

  // --- save (bridged by the pending-changes panel) ---

  setupSnaphotProxy(proxy: any): void {
    this.snapshotProxy = proxy;
  }

  saveChanges(changes: any[]): any {
    const updates = this.getUpdates(changes);
    return this.property.saveEmailsData(this.propertyUuid(), updates);
  }

  private getUpdates(changes: any[]): any[] {
    // ⭐ native-diff paths are rooted at model.emailsData (the panel's [live]): a per-leaf edit is
    // [i,'languages',j,field] → emailsData index = path[0], languages index = path[2] (v1
    // vm.model.emailsData was path[3]/[5]). deep-diff can also emit array-level (kind:'A') changes
    // with no usable flat path — filter to the well-formed language-content changes only.
    // Flatten deep-diff kind:'A' array changes into flat index paths ([i,'languages',j,field]),
    // then keep only the well-formed language-content edits (spurious whole-array 'N' diffs from a
    // stale snapshot flatten to [idx] and are dropped by the path[1]==='languages' guard).
    let updatedData = (changes || [])
      .map((c: any) => flattenChange(c))
      .filter((c: any) => Array.isArray(c.path) && c.path[1] === 'languages' && typeof c.path[0] === 'number' && typeof c.path[2] === 'number')
      .map((c: any) => {
        const chngObj = this.model().emailsData[c.path[0]];
        // angular.extend({}, a, b) ≡ Object.assign for plain objects (inc 2t —
        // drops the window.angular load-order dependency).
        return Object.assign({}, { emailType: chngObj.emailType }, chngObj.languages[c.path[2]]);
      });
    updatedData = reduce(
      updatedData,
      (result: any[], value: any) => {
        const isThere = !!find(result, (r: any) => r.emailType === value.emailType && r.code === value.code);
        if (!isThere) result.push(value);
        return result;
      },
      [],
    );
    updatedData = _map(updatedData, (d: any) => ({
      emailType: d.emailType,
      languageCode: d.code,
      subject: d.subject,
      body: d.body,
      footer: d.footer,
      contestContent: d.contest_content,
    }));
    return updatedData;
  }
}

/**
 * Flatten a deep-diff `kind:'A'` (array) change into a flat index path so an in-place nested edit
 * reads as `{kind:'E', path:[i,'languages',j,'subject'], …}`. The native diff is rooted at the
 * emailsData ARRAY, so deep-diff emits nested `kind:'A'` (path-to-array + index + item) instead of
 * the flat object-recursion paths the v1 scope-rooted diff produced.
 */
function flattenChange(c: any, base: any[] = []): any {
  if (c && c.kind === 'A') {
    return flattenChange(c.item, base.concat(c.path || []).concat([c.index]));
  }
  return Object.assign({}, c, { path: base.concat((c && c.path) || []) });
}
